import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Eye, EyeOff, Mail, Lock, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiError, LOGO_URL } from "@/lib/api";

const IMG_LEFT =
    "https://static.prod-images.emergentagent.com/jobs/6c3dbc7e-e3b1-4c5d-b777-4fea74d38e17/images/71b0a993a7d7787fcbe4eee6426080ef22d96ef28cef1c675aac5f46b7f79fcf.jpeg";

function redirectTargetForUser(user) {
    if (!user) return "/";
    if (user.role === "admin") return "/admin";
    if (user.role === "formateur") {
        if (user.must_change_password || !user.onboarding_completed) return "/formateur/onboarding";
        return "/formateur";
    }
    if (user.role === "stagiaire") return "/stagiaire";
    return "/";
}

export default function Login() {
    const { user, login, loading } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!loading && user) {
            const target = location.state?.from || redirectTargetForUser(user);
            navigate(target, { replace: true });
        }
    }, [user, loading, navigate, location.state]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        setSubmitting(true);
        try {
            const u = await login(email.trim(), password);
            toast.success(`Bienvenue, ${u.first_name}`);
            const target = redirectTargetForUser(u);
            navigate(target, { replace: true });
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="grid min-h-screen grid-cols-1 lg:grid-cols-2" data-testid="page-login">
            {/* Left — image + manifesto */}
            <div className="relative hidden overflow-hidden bg-brand-ink text-white lg:block">
                <img
                    src={IMG_LEFT}
                    alt="Cursus Physio"
                    className="absolute inset-0 h-full w-full object-cover opacity-70"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-brand-ink via-brand-ink/70 to-brand-ink/85" />
                <div className="absolute inset-0 grain opacity-40" />
                <div className="relative z-10 flex h-full flex-col justify-between p-14">
                    <Link to="/" className="flex items-center gap-3" data-testid="login-logo-link">
                        <img
                            src={LOGO_URL}
                            alt="Cursus Physio"
                            className="h-10 w-auto brightness-0 invert"
                        />
                    </Link>
                    <div>
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                            className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/[0.06] px-4 py-1.5 text-[11px] uppercase tracking-[0.28em] backdrop-blur"
                        >
                            <Sparkles className="h-3.5 w-3.5 text-brand-orange" />
                            Espace privé
                        </motion.div>
                        <motion.h1
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
                            className="mt-8 font-display text-5xl font-normal leading-[0.95] tracking-tightest lg:text-7xl"
                        >
                            La <em className="italic text-brand-orange">Prévention Active</em> <br />
                            se construit ici.
                        </motion.h1>
                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.9, delay: 0.3 }}
                            className="mt-8 max-w-md text-base text-white/70 leading-relaxed"
                        >
                            Accédez à votre espace pour gérer vos programmes, vos disponibilités et vos villes d'intervention.
                        </motion.p>
                    </div>
                    <div className="text-xs text-white/40">
                        © {new Date().getFullYear()} Cursus Physio · Espace privé sécurisé
                    </div>
                </div>
            </div>

            {/* Right — form */}
            <div className="flex items-center justify-center bg-[#FAFAFA] px-6 py-16">
                <div className="w-full max-w-md">
                    <div className="mb-10 lg:hidden">
                        <img src={LOGO_URL} alt="Cursus Physio" className="h-10 w-auto" />
                    </div>
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                    >
                        <h2 className="font-display text-5xl font-normal tracking-tightest">
                            Bienvenue.
                        </h2>
                        <p className="mt-3 text-brand-ink/60">
                            Connectez-vous à votre espace Cursus Physio.
                        </p>
                    </motion.div>

                    <form onSubmit={handleSubmit} className="mt-10 space-y-5" data-testid="login-form">
                        <div>
                            <label htmlFor="email" className="block text-xs font-semibold uppercase tracking-wider text-brand-ink">
                                Email
                            </label>
                            <div className="relative mt-2">
                                <Mail className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-smoke" />
                                <input
                                    id="email"
                                    type="email"
                                    required
                                    autoComplete="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    data-testid="login-input-email"
                                    className="w-full rounded-xl border border-brand-line bg-white py-3.5 pl-11 pr-4 text-base outline-none transition-all focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20"
                                />
                            </div>
                        </div>
                        <div>
                            <div className="flex items-center justify-between">
                                <label htmlFor="password" className="block text-xs font-semibold uppercase tracking-wider text-brand-ink">
                                    Mot de passe
                                </label>
                                <Link
                                    to="/forgot-password"
                                    className="text-xs text-brand-orange hover:underline"
                                    data-testid="login-forgot-password"
                                >
                                    Mot de passe oublié ?
                                </Link>
                            </div>
                            <div className="relative mt-2">
                                <Lock className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-smoke" />
                                <input
                                    id="password"
                                    type={showPassword ? "text" : "password"}
                                    required
                                    autoComplete="current-password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    data-testid="login-input-password"
                                    className="w-full rounded-xl border border-brand-line bg-white py-3.5 pl-11 pr-12 text-base outline-none transition-all focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword((v) => !v)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-smoke hover:text-brand-ink"
                                    aria-label={showPassword ? "Masquer" : "Afficher"}
                                    data-testid="login-toggle-password"
                                >
                                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="login-submit"
                            className="group mt-2 inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-orange px-6 py-4 text-sm font-semibold text-white transition-all hover:bg-[#d9772c] hover:shadow-lg hover:shadow-brand-orange/20 disabled:opacity-60"
                        >
                            {submitting ? "Connexion…" : (<>Se connecter <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></>)}
                        </button>

                        <div className="pt-4 text-center text-sm text-brand-smoke">
                            Pas encore de compte ?{" "}
                            <Link to="/signup" data-testid="login-signup-link" className="font-semibold text-brand-orange hover:underline">
                                Créer un compte stagiaire
                            </Link>
                        </div>

                        <div className="pt-3 text-center text-xs text-brand-smoke">
                            <Link to="/" className="hover:text-brand-orange" data-testid="login-back-home">
                                ← Retour au site
                            </Link>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}
